import { useState, useEffect, useCallback } from "react";
import { useParams } from "react-router-dom";
import { fetchPublicInfo, fetchPublicAll } from "../services/dashboardApi";
import { useDashboardData } from "../hooks/useDashboardData";
import { useDashboardAccessGuard } from "../hooks/useDashboardAccessGuard";
import DashboardShell from "../components/DashboardShell";
import OverviewSection from "../components/sections/OverviewSection";
import InspectionsSection from "../components/sections/InspectionsSection";
import IncidentsSection from "../components/sections/IncidentsSection";
import CredentialsSection from "../components/sections/CredentialsSection";
import AssetsSection from "../components/sections/AssetsSection";
import BenchmarksSection from "../components/sections/BenchmarksSection";
import UsersSection from "../components/sections/UsersSection";
import SubcontractorsSection from "../components/sections/SubcontractorsSection";
import Loader from "../../../library/common/Loader";
import "../reportDashboard.scss";

const FIVE_MINUTES = 5 * 60 * 1000;

const PublicDashboardPage = () => {
  const { token } = useParams();
  const [info, setInfo] = useState(null);
  const [dateRange, setDateRange] = useState({ preset: "all", from: null, to: null });

  // Errors swallowed on purpose — the /all request below is authoritative for the 404 state.
  useEffect(() => {
    fetchPublicInfo(token)
      .then(setInfo)
      .catch(() => {});
  }, [token]);

  const { data, loading, refetching, notFound, error, markAccessRevoked } = useDashboardData(
    () => fetchPublicAll(token, { from: dateRange.from, to: dateRange.to }),
    [token, dateRange.from, dateRange.to],
    { silentRefetchMs: FIVE_MINUTES }
  );

  // Any request from any section that comes back 404 means the share link was
  // revoked or expired — swap to the invalid-link screen immediately instead of
  // waiting for the 5-minute silent refetch or a manual page reload.
  useDashboardAccessGuard(markAccessRevoked);

  // Liveness probe on section switch. Info is the cheapest public endpoint —
  // a single token-row lookup, no dashboard aggregation and no access-log
  // write. The 404 it returns on a revoked token is what raises the signal via
  // dashboardApi's guard; the catch here only stops an unhandled rejection.
  const validateAccess = useCallback(() => {
    fetchPublicInfo(token).catch(() => {});
  }, [token]);

  if (notFound) {
    return (
      <div className="rdPublicMessage">
        <h2>This link is invalid or has expired.</h2>
        <p>Please contact HB NEXT for an updated link.</p>
      </div>
    );
  }

  // `refetching` is checked too, not just `loading`: useDashboardData retries once
  // on a non-404 failure, and that second attempt is a refetch. Without this the
  // page would fall through to the shell mid-retry with no payload, and the
  // sections read `data` directly.
  if ((loading || refetching) && !data) {
    return <Loader />;
  }

  if (error && !data) {
    return (
      <div className="rdPublicMessage">
        <h2>Something went wrong loading this dashboard.</h2>
        <p>Please try again shortly.</p>
      </div>
    );
  }

  const clientName = data?.client?.clientName || info?.clientName || "";
  // No accent on the payload → applyAccentColor clears the custom property and
  // reportDashboard.scss falls back to the app's primary colour.
  const accentColor = data?.client?.accentColor || info?.accentColor || null;

  const apiCtx = { mode: "public", token, from: dateRange.from, to: dateRange.to };

  return (
    <div className="rdPage rdPage--public">
      <DashboardShell
        clientName={clientName}
        accentColor={accentColor}
        dateRange={dateRange}
        onDateRangeChange={setDateRange}
        refetching={refetching}
        onSectionChange={validateAccess}
        sections={[
        <OverviewSection key="overview" data={data} apiCtx={apiCtx} refetching={refetching} />,
        <InspectionsSection key="inspections" data={data} apiCtx={apiCtx} refetching={refetching} />,
        <IncidentsSection key="incidents" data={data} apiCtx={apiCtx} refetching={refetching} />,
        <CredentialsSection key="credentials" data={data} apiCtx={apiCtx} refetching={refetching} />,
        <AssetsSection key="assets" data={data} apiCtx={apiCtx} refetching={refetching} />,
        <BenchmarksSection key="benchmarks" data={data} dateRange={dateRange} />,
          <UsersSection key="users" data={data} apiCtx={apiCtx} />,
          <SubcontractorsSection key="subcontractors" data={data} apiCtx={apiCtx} refetching={refetching} />,
        ]}
      />
    </div>
  );
};

export default PublicDashboardPage;
